import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import type { Editor } from '@tiptap/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '../../tiptap-icons/check-icon';
import { MessageSquareIcon } from '../../tiptap-icons/message-square-icon';
import { ThreadItem } from './thread-item';
import { useThreadPositions } from './use-thread-positions';
import { useThreads } from './use-threads';

type Tab = 'open' | 'resolved';

export interface ThreadsPanelProps {
  editor: Editor;
  provider: TiptapCollabProvider;
  userName: string;
  userAvatar?: string;
}

export function ThreadsPanel({ editor, provider, userName, userAvatar }: ThreadsPanelProps) {
  const { threads, loaded } = useThreads(provider);
  const [activeTab, setActiveTab] = useState<Tab>('open');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const syncFromEditor = () => {
      const focused: string[] = editor.storage.comments?.focusedThreads ?? [];
      const openFocused = focused.filter((id) => {
        const thread = provider.getThread(id);
        return thread && !thread.resolvedAt;
      });
      setSelectedThreadId(openFocused[0] ?? null);
    };

    syncFromEditor();
    editor.on('selectionUpdate', syncFromEditor);
    return () => {
      editor.off('selectionUpdate', syncFromEditor);
    };
  }, [editor, provider]);

  const filteredThreads = useMemo(
    () => threads.filter((t) => (activeTab === 'open' ? !t.resolvedAt : !!t.resolvedAt)),
    [threads, activeTab],
  );

  const { positions, orphanedIds } = useThreadPositions(editor, filteredThreads, cardRefs, listRef);

  const setCardRef = useCallback((threadId: string, el: HTMLDivElement | null) => {
    if (el) {
      cardRefs.current.set(threadId, el);
    } else {
      cardRefs.current.delete(threadId);
    }
  }, []);

  const handleSelectThread = useCallback(
    (threadId: string) => {
      const nextId = selectedThreadId === threadId ? null : threadId;
      setSelectedThreadId(nextId);

      if (nextId) {
        editor.commands.selectThread({ id: nextId, updateSelection: false });
      } else {
        editor.commands.unselectThread();
      }
    },
    [editor, selectedThreadId],
  );

  return (
    <div className="threads-panel">
      <div className="threads-panel-toggle">
        <button
          type="button"
          className={`threads-panel-toggle-btn ${activeTab === 'open' ? 'active' : ''}`}
          onClick={() => setActiveTab('open')}
          title="Open comments"
        >
          <MessageSquareIcon width={14} height={14} />
        </button>
        <button
          type="button"
          className={`threads-panel-toggle-btn ${activeTab === 'resolved' ? 'active' : ''}`}
          onClick={() => setActiveTab('resolved')}
          title="Resolved comments"
        >
          <CheckIcon width={14} height={14} />
        </button>
      </div>

      <div ref={listRef} className="threads-panel-list">
        {loaded && filteredThreads.length === 0 ? (
          <div className="threads-panel-empty">No {activeTab} comments</div>
        ) : (
          filteredThreads.map((thread) => {
            const top = positions.get(thread.id);
            if (top === undefined) return null;

            return (
              <div
                key={thread.id}
                ref={(el) => setCardRef(thread.id, el)}
                className="threads-panel-positioned-card"
                style={{ top }}
              >
                <ThreadItem
                  thread={thread}
                  editor={editor}
                  provider={provider}
                  userName={userName}
                  userAvatar={userAvatar}
                  isSelected={selectedThreadId === thread.id}
                  isOrphaned={orphanedIds.has(thread.id)}
                  onSelect={handleSelectThread}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
